import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import moment from "moment";
import {
  loadFiltersFromStorage,
  saveFiltersToStorage,
  clearFiltersFromStorage,
} from "../utils/filterStorage";
import { sortingVariables } from "../CredentialGlobalTable";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import {
  DEFAULT_DOCUMENTATION_STATUS,
  isDocumentationStatusFiltered,
  normalizeDocumentationStatus,
} from "../CredentialTypeStatus";

export const useCredentialsFilter = (activeTab) => {
  // Filter states (temporary - for UI)
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [credentialTypes, setCredentialTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [selectedEmpFilter, setSelectedEmpFilter] = useState([]);
  const [selectedOfficeFilter, setSelectedOfficeFilter] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [searchValueApplied, setSearchValueApplied] = useState("");
  const [radioChecked, setRadioChecked] = useState(false);
  const [documentationStatus, setDocumentationStatus] = useState([
    ...DEFAULT_DOCUMENTATION_STATUS,
  ]);
  const { selectedId } = useAccountOwnerCommon();
  const preservedRef = useRef(null);
  // Applied filter states (used for API calls)
  const [startDateApplied, setStartDateApplied] = useState(null);
  const [endDateApplied, setEndDateApplied] = useState(null);
  const [credentialTypesApplied, setCredentialTypesApplied] = useState([]);
  const [statusesApplied, setStatusesApplied] = useState([]);
  const [selectedEmpFilterApplied, setSelectedEmpFilterApplied] = useState([]);
  const [selectedOfficeFilterApplied, setSelectedOfficeFilterApplied] =
    useState([]);
  const [radioCheckedApplied, setRadioCheckedApplied] = useState(false);
  const [documentationStatusApplied, setDocumentationStatusApplied] = useState([
    ...DEFAULT_DOCUMENTATION_STATUS,
  ]);

  // Sorting
  const [sortBy, setSortBy] = useState(sortingVariables.UserName);
  const [isDescending, setIsDescending] = useState(true);

  // Pagination
  const [pageNumber, setPageNumber] = useState(1);

  // Reseting filter state
  const [resetSearchTrigger, setResetSearchTrigger] = useState(0);
  const isFilterApplied = useMemo(() => {
    return (
      credentialTypesApplied.length > 0 ||
      statusesApplied.length > 0 ||
      (activeTab === "1" && selectedEmpFilterApplied.length > 0) ||
      (activeTab === "2" && selectedOfficeFilterApplied.length > 0) ||
      startDateApplied !== null ||
      endDateApplied !== null ||
      radioCheckedApplied ||
      isDocumentationStatusFiltered(documentationStatusApplied)
    );
  }, [
    credentialTypesApplied,
    statusesApplied,
    selectedEmpFilterApplied,
    selectedOfficeFilterApplied,
    startDateApplied,
    endDateApplied,
    activeTab,
    radioCheckedApplied,
    documentationStatusApplied,
  ]);
  // Load filters from localStorage on mount and when tab changes
  useEffect(() => {
    const savedFilters = loadFiltersFromStorage(activeTab);
    if (savedFilters) {
      if (savedFilters.startDateApplied) {
        const date = new Date(savedFilters.startDateApplied);
        setStartDateApplied(savedFilters.startDateApplied);
        setStartDate(date);
      } else {
        setStartDate(null);
        setStartDateApplied(null);
      }
      if (savedFilters.endDateApplied) {
        const date = new Date(savedFilters.endDateApplied);
        setEndDateApplied(savedFilters.endDateApplied);
        setEndDate(date);
      } else {
        setEndDate(null);
        setEndDateApplied(null);
      }
      if (savedFilters.credentialTypesApplied) {
        setCredentialTypes(savedFilters.credentialTypesApplied);
        setCredentialTypesApplied(savedFilters.credentialTypesApplied);
      } else {
        setCredentialTypes([]);
        setCredentialTypesApplied([]);
      }
      if (savedFilters.statusesApplied) {
        setStatuses(savedFilters.statusesApplied);
        setStatusesApplied(savedFilters.statusesApplied);
      } else {
        setStatuses([]);
        setStatusesApplied([]);
      }
      if (savedFilters.selectedEmpFilterApplied) {
        setSelectedEmpFilter(savedFilters.selectedEmpFilterApplied);
        setSelectedEmpFilterApplied(savedFilters.selectedEmpFilterApplied);
      } else {
        setSelectedEmpFilter([]);
        setSelectedEmpFilterApplied([]);
      }
      if (savedFilters.selectedOfficeFilterApplied) {
        setSelectedOfficeFilter(savedFilters.selectedOfficeFilterApplied);
        setSelectedOfficeFilterApplied(
          savedFilters.selectedOfficeFilterApplied,
        );
      } else {
        setSelectedOfficeFilter([]);
        setSelectedOfficeFilterApplied([]);
      }
      if (
        savedFilters.sortBy &&
        savedFilters.sortBy !== sortingVariables.IssuingAuthority
      ) {
        setSortBy(savedFilters.sortBy);
      } else {
        setSortBy(sortingVariables.UserName);
      }
      if (savedFilters.isDescending !== undefined) {
        setIsDescending(savedFilters.isDescending);
      } else {
        setIsDescending(true);
      }
      if (savedFilters.radioCheckedApplied !== undefined) {
        setRadioChecked(savedFilters.radioCheckedApplied);
        setRadioCheckedApplied(savedFilters.radioCheckedApplied);
      } else {
        setRadioChecked(false);
        setRadioCheckedApplied(false);
      }
      if (savedFilters.documentationStatusApplied !== undefined) {
        const normalized = normalizeDocumentationStatus(
          savedFilters.documentationStatusApplied,
        );
        setDocumentationStatus(normalized);
        setDocumentationStatusApplied(normalized);
      } else {
        setDocumentationStatus([...DEFAULT_DOCUMENTATION_STATUS]);
        setDocumentationStatusApplied([...DEFAULT_DOCUMENTATION_STATUS]);
      }
    }
    setPageNumber(1);
  }, [activeTab]);

  // Update sortBy default when tab changes (only if no saved filters)
  useEffect(() => {
    if (!loadFiltersFromStorage(activeTab)) {
      setSortBy(1);
    }
  }, [activeTab]);

  const handleApplyFilter = () => {
    const newStates = {
      startDateApplied: startDate
        ? moment(startDate).format("YYYY-MM-DD")
        : null,
      endDateApplied: endDate ? moment(endDate).format("YYYY-MM-DD") : null,
      credentialTypesApplied: [...credentialTypes],
      statusesApplied: [...statuses],
      selectedEmpFilterApplied: [...selectedEmpFilter],
      selectedOfficeFilterApplied: [...selectedOfficeFilter],
      sortBy: sortBy,
      isDescending: isDescending,
      radioCheckedApplied: radioChecked,
      documentationStatusApplied: [...documentationStatus],
    };
    if (startDate && !endDate) {
      const endDate = moment(startDate).add(7, "day").format("YYYY-MM-DD");
      setEndDate(endDate);
      newStates.endDateApplied = endDate;
    }
    if (endDate && !startDate) {
      const startDate = moment(endDate).subtract(7, "day").format("YYYY-MM-DD");
      setStartDate(startDate);
      newStates.startDateApplied = startDate;
    }
    // Apply all new states to applied states
    setStartDateApplied(newStates.startDateApplied);
    setEndDateApplied(newStates.endDateApplied);
    setCredentialTypesApplied(newStates.credentialTypesApplied);
    setStatusesApplied(newStates.statusesApplied);
    setSelectedEmpFilterApplied(newStates.selectedEmpFilterApplied);
    setSelectedOfficeFilterApplied(newStates.selectedOfficeFilterApplied);
    setRadioCheckedApplied(radioChecked);
    setDocumentationStatusApplied(newStates.documentationStatusApplied);
    setPageNumber(1);

    // Save to localStorage
    saveFiltersToStorage(newStates, activeTab);
  };

  const resetFilter = () => {
    // Reset all filter states
    setRadioChecked(false);
    setRadioCheckedApplied(false);
    setStartDate(null);
    setStartDateApplied(null);
    setEndDate(null);
    setEndDateApplied(null);
    setCredentialTypes([]);
    setCredentialTypesApplied([]);
    setStatuses([]);
    setStatusesApplied([]);
    setSelectedEmpFilter([]);
    setSelectedEmpFilterApplied([]);
    setSelectedOfficeFilter([]);
    setSelectedOfficeFilterApplied([]);
    setSelectedRoles([]);
    setSearchValue("");
    setSearchValueApplied("");
    setDocumentationStatus([...DEFAULT_DOCUMENTATION_STATUS]);
    setDocumentationStatusApplied([...DEFAULT_DOCUMENTATION_STATUS]);
    setSortBy(sortingVariables.UserName);
    setIsDescending(true);
    setPageNumber(1);
    setResetSearchTrigger((prev) => prev + 1);
    // Clear localStorage
    clearFiltersFromStorage(activeTab);
  };
  useEffect(() => {
    if (selectedId) {
      if (preservedRef.current === null) {
        preservedRef.current = selectedId;
      } else if (preservedRef.current !== selectedId) {
        resetFilter();
        preservedRef.current = selectedId;
      }
    }
  }, [selectedId]);
  // Wrapper for setSortBy that also saves to localStorage
  const handleSetSortByAndIsDescending = useCallback(
    (newSortBy, newIsDescending) => {
      setSortBy(newSortBy);
      setIsDescending(newIsDescending);
      setPageNumber(1);
      const existingFilters = loadFiltersFromStorage(activeTab) || {};
      saveFiltersToStorage(
        {
          ...existingFilters,
          sortBy: newSortBy,
          isDescending: newIsDescending,
        },
        activeTab,
      );
    },
    [activeTab],
  );

  const handleSetSearchValueApplied = useCallback((value) => {
    setSearchValueApplied(value);
    setPageNumber(1);
  }, []);

  return {
    // Filter states
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    credentialTypes,
    setCredentialTypes,
    statuses,
    setStatuses,
    selectedEmpFilter,
    setSelectedEmpFilter,
    selectedOfficeFilter,
    setSelectedOfficeFilter,
    selectedRoles,
    setSelectedRoles,
    searchValue,
    setSearchValue,
    radioChecked,
    setRadioChecked,
    setRadioCheckedApplied,
    radioCheckedApplied,
    documentationStatus,
    setDocumentationStatus,
    isFilterApplied,
    searchValueApplied,
    setSearchValueApplied: handleSetSearchValueApplied,
    pageNumber,
    setPageNumber,
    // Applied filter states
    startDateApplied,
    endDateApplied,
    credentialTypesApplied,
    statusesApplied,
    selectedEmpFilterApplied,
    selectedOfficeFilterApplied,
    documentationStatusApplied,

    // Sorting
    sortBy,
    isDescending,
    handleSetSortByAndIsDescending,

    // Actions
    handleApplyFilter,
    resetFilter,
    resetSearchTrigger,
  };
};
